import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Calendar,
  MapPin,
  Clock,
  ChevronRight,
  Edit,
  Trash2,
  Share2,
  CheckCircle,
  Map,
  Navigation,
  X,
  Search,
  Sparkles,
  Coffee,
  Utensils,
  Building,
  Tag,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Save,
  Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { tripsApi, placesApi } from '../services/api';

const MyTripsPage = ({ onTabChange }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { darkMode } = useTheme();

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  const [activeTab, setActiveTab] = useState('upcoming');
  const [showNewTripModal, setShowNewTripModal] = useState(false);
  const [showAddPlaceModal, setShowAddPlaceModal] = useState(false);
  const [showAiPlannerModal, setShowAiPlannerModal] = useState(false);
  const [showReadyRoutesModal, setShowReadyRoutesModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedReadyRoute, setSelectedReadyRoute] = useState(null);
  const [newTrip, setNewTrip] = useState({ name: '', date: '', duration: '1 gün' });
  const [trips, setTrips] = useState([]);
  const [places, setPlaces] = useState([]);
  const [placeSearch, setPlaceSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // AI Planner State
  const [aiMode, setAiMode] = useState('form'); // 'form' or 'chat'
  const [aiDuration, setAiDuration] = useState(1);
  const [aiInterests, setAiInterests] = useState([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPlan, setAiPlan] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});

  const interestOptions = [
    { id: 'tarih', label: 'Tarih', icon: '🏛️' },
    { id: 'doga', label: 'Doğa', icon: '🌲' },
    { id: 'yemek', label: 'Yemek', icon: '🍽️' },
    { id: 'kultur', label: 'Kültür', icon: '🎭' },
    { id: 'macera', label: 'Macera', icon: '🏔️' },
    { id: 'deniz', label: 'Deniz', icon: '🌊' },
  ];

  // Ready-made routes
  const readyRoutes = [
    {
      id: 'route-1',
      name: 'Tarihi Giresun Turu',
      description: 'Giresun\'un tarihi dokusunu keşfedin. Kale, müze ve tarihi mahalleler.',
      duration: '1 gün',
      difficulty: 'Kolay',
      category: 'Tarihi',
      image: 'https://images.unsplash.com/photo-1568454537842-d933259bb258?w=400&q=80',
      rating: 4.8,
      reviewCount: 124,
      places: [
        { id: 'p1', name: 'Giresun Kalesi', time: '09:00', duration: '1.5 saat', type: 'place' },
        { id: 'p2', name: 'Giresun Müzesi', time: '11:00', duration: '1 saat', type: 'place' },
        { id: 'p3', name: 'Fındık Lokantası', time: '12:30', duration: '1 saat', type: 'restaurant', has_campaign: true, campaign_text: '%15 İndirim' },
        { id: 'p4', name: 'Zeytinlik Mahallesi', time: '14:00', duration: '2 saat', type: 'place' },
        { id: 'p5', name: 'Sahil Yürüyüşü', time: '16:30', duration: '1 saat', type: 'place' },
      ],
      tips: ['Rahat ayakkabı giyin', 'Fotoğraf makinenizi unutmayın', 'Öğle yemeği için rezervasyon yapın'],
    },
    {
      id: 'route-2',
      name: 'Doğa & Şelale Rotası',
      description: 'Giresun\'un muhteşem doğasını keşfedin. Şelaleler, göller ve yaylalar.',
      duration: '2 gün',
      difficulty: 'Orta',
      category: 'Doğa',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=80',
      rating: 4.9,
      reviewCount: 89,
      places: [
        { id: 'p6', name: 'Kuzalan Şelalesi', time: '09:00', duration: '2 saat', type: 'place' },
        { id: 'p7', name: 'Mavi Göl', time: '12:00', duration: '1.5 saat', type: 'place' },
        { id: 'p8', name: 'Yayla Kahvaltısı', time: '08:00', duration: '1 saat', type: 'restaurant', day: 2 },
        { id: 'p9', name: 'Bektaş Yaylası', time: '10:00', duration: '3 saat', type: 'place', day: 2 },
      ],
      tips: ['Trekking ayakkabısı şart', 'Yağmurluk alın', 'Bol su götürün'],
    },
    {
      id: 'route-3',
      name: 'Lezzet Rotası',
      description: 'Giresun\'un eşsiz mutfağını tadın. Karadeniz lezzetleri ve fındık tatlıları.',
      duration: '1 gün',
      difficulty: 'Kolay',
      category: 'Yeme-İçme',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&q=80',
      rating: 4.7,
      reviewCount: 156,
      places: [
        { id: 'p10', name: 'Karadeniz Kahvaltısı', time: '09:00', duration: '1.5 saat', type: 'restaurant' },
        { id: 'p11', name: 'Fındık Fabrikası Turu', time: '11:00', duration: '1 saat', type: 'place' },
        { id: 'p12', name: 'Balık Restoranı', time: '13:00', duration: '1.5 saat', type: 'restaurant', has_campaign: true, campaign_text: 'Tatlı İkramı' },
        { id: 'p13', name: 'Geleneksel Pastane', time: '15:00', duration: '45 dk', type: 'cafe' },
        { id: 'p14', name: 'Sahil Kafesi', time: '16:30', duration: '1 saat', type: 'cafe', has_campaign: true, campaign_text: '2. Kahve Bedava' },
      ],
      tips: ['Aç gelin!', 'Fındıklı baklava mutlaka deneyin', 'Yerel çayı kaçırmayın'],
    },
    {
      id: 'route-4',
      name: 'Fotoğraf Rotası',
      description: 'Giresun\'un en fotoğrafik noktaları. Gün batımı ve manzara noktaları.',
      duration: '1 gün',
      difficulty: 'Kolay',
      category: 'Fotoğraf',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80',
      rating: 4.6,
      reviewCount: 78,
      places: [
        { id: 'p15', name: 'Giresun Kalesi (Gün Doğumu)', time: '06:00', duration: '1.5 saat', type: 'place' },
        { id: 'p16', name: 'Zeytinlik Sokakları', time: '08:30', duration: '2 saat', type: 'place' },
        { id: 'p17', name: 'Giresun Adası Manzarası', time: '11:00', duration: '1 saat', type: 'place' },
        { id: 'p18', name: 'Sahil Parkı (Gün Batımı)', time: '17:00', duration: '1.5 saat', type: 'place' },
      ],
      tips: ['Tripod getirin', 'Gün doğumu için erken kalkın', 'Yedek batarya alın'],
    },
    {
      id: 'route-5',
      name: 'Hafta Sonu Kaçamağı',
      description: '2 günlük mükemmel Giresun deneyimi. Tarih, doğa ve lezzet bir arada.',
      duration: '2 gün',
      difficulty: 'Orta',
      category: 'Karma',
      image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&q=80',
      rating: 4.9,
      reviewCount: 234,
      places: [
        { id: 'p19', name: 'Giresun Kalesi', time: '10:00', duration: '1.5 saat', type: 'place' },
        { id: 'p20', name: 'Fındık Lokantası', time: '12:00', duration: '1 saat', type: 'restaurant', has_campaign: true, campaign_text: '%10 İndirim' },
        { id: 'p21', name: 'Zeytinlik Mahallesi', time: '14:00', duration: '2 saat', type: 'place' },
        { id: 'p22', name: 'Kuzalan Şelalesi', time: '09:00', duration: '2 saat', type: 'place', day: 2 },
        { id: 'p23', name: 'Bektaş Yaylası', time: '12:00', duration: '3 saat', type: 'place', day: 2 },
      ],
      tips: ['Otel rezervasyonu yapın', 'Rahat kıyafetler tercih edin', '2. gün için araç kiralayın'],
    },
  ];

  // Default trips for demo
  const defaultTrips = [
    {
      id: '1',
      name: 'Tarihi Giresun Turu',
      date: '25 Ocak 2025',
      duration: '1 gün',
      status: 'upcoming',
      places: [
        { id: 'place-1', name: 'Giresun Kalesi', completed: false },
        { id: 'place-3', name: 'Giresun Müzesi', completed: false },
        { id: 'place-4', name: 'Zeytinlik Mahallesi', completed: false },
      ],
      image: 'https://images.unsplash.com/photo-1568454537842-d933259bb258?w=400&q=80',
    },
    {
      id: '2',
      name: 'Doğa Kaçamağı',
      date: '15 Şubat 2025',
      duration: '2 gün',
      status: 'upcoming',
      places: [
        { id: 'place-5', name: 'Kuzalan Şelalesi', completed: false },
        { id: 'place-6', name: 'Mavi Göl', completed: false },
        { id: 'place-7', name: 'Bektaş Yaylası', completed: false },
      ],
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=80',
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        const [tripsData, placesData] = await Promise.all([
          tripsApi.getAll().catch(() => []),
          placesApi.getFeatured()
        ]);
        
        setTrips(tripsData.length > 0 ? tripsData : defaultTrips);
        setPlaces(placesData);
      } catch (error) {
        console.error('Error fetching trips:', error);
        setTrips(defaultTrips);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated]);

  const upcomingTrips = trips.filter(t => t.status === 'upcoming');
  const completedTrips = trips.filter(t => t.status === 'completed');
  const displayTrips = activeTab === 'upcoming' ? upcomingTrips : completedTrips;

  const filteredPlaces = places.filter(p => 
    p.name.toLowerCase().includes(placeSearch.toLowerCase())
  );

  const toggleInterest = (id) => {
    setAiInterests(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectReadyRoute = (route) => {
    setSelectedReadyRoute(route);
  };

  const handleAddReadyRouteToTrips = async () => {
    if (!selectedReadyRoute) return;
    
    try {
      const routeData = {
        name: selectedReadyRoute.name,
        duration: selectedReadyRoute.duration,
        description: selectedReadyRoute.description,
        category: selectedReadyRoute.category,
        image: selectedReadyRoute.image,
        rating: selectedReadyRoute.rating,
        places: selectedReadyRoute.places,
        tips: selectedReadyRoute.tips,
      };
      
      const result = await tripsApi.addReadyRoute(routeData);
      
      if (result.success) {
        setTrips([result.trip, ...trips]);
        setShowReadyRoutesModal(false);
        setSelectedReadyRoute(null);
        alert('Hazır rota gezilerinize eklendi! 🎉');
      }
    } catch (error) {
      console.error('Ready route add error:', error);
      // Fallback to local storage if backend fails
      const today = new Date();
      const newTrip = {
        id: `ready-${Date.now()}`,
        name: selectedReadyRoute.name,
        date: today.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }),
        duration: selectedReadyRoute.duration,
        status: 'upcoming',
        places: selectedReadyRoute.places.map(p => ({
          ...p,
          completed: false
        })),
        tips: selectedReadyRoute.tips,
        image: selectedReadyRoute.image,
        ready_route: true,
      };
      
      setTrips([newTrip, ...trips]);
      setShowReadyRoutesModal(false);
      setSelectedReadyRoute(null);
      alert('Rota eklendi (yerel)');
    }
  };

  const handleGenerateAiTrip = async () => {
    if (aiMode === 'form' && aiInterests.length === 0) {
      alert('Lütfen en az bir ilgi alanı seçin');
      return;
    }
    if (aiMode === 'chat' && !aiPrompt.trim()) {
      alert('Lütfen ne tür bir gezi istediğinizi yazın');
      return;
    }

    setAiLoading(true);
    try {
      const result = await tripsApi.generateAiTrip(
        aiDuration,
        aiInterests,
        aiMode === 'chat' ? aiPrompt : null
      );
      
      if (result.success) {
        setAiPlan(result.plan);
        // Expand all days by default
        const expanded = {};
        result.plan.days?.forEach((_, i) => expanded[i] = true);
        setExpandedDays(expanded);
      }
    } catch (error) {
      console.error('AI trip generation error:', error);
      alert('Rota oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveAiTrip = async () => {
    if (!aiPlan) return;
    
    try {
      const result = await tripsApi.saveAiTrip(
        aiPlan.name,
        aiPlan.duration,
        aiPlan
      );
      
      if (result.success) {
        setTrips([result.trip, ...trips]);
        setShowAiPlannerModal(false);
        setAiPlan(null);
        setAiInterests([]);
        setAiPrompt('');
      }
    } catch (error) {
      console.error('Save AI trip error:', error);
      alert('Rota kaydedilirken bir hata oluştu.');
    }
  };

  const handleCreateTrip = async () => {
    if (!newTrip.name || !newTrip.date) return;
    
    try {
      const trip = await tripsApi.create(newTrip.name, newTrip.date, newTrip.duration);
      setTrips([trip, ...trips]);
    } catch (error) {
      const trip = {
        id: Date.now().toString(),
        name: newTrip.name,
        date: newTrip.date,
        duration: newTrip.duration,
        status: 'upcoming',
        places: [],
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80',
      };
      setTrips([trip, ...trips]);
    }
    
    setNewTrip({ name: '', date: '', duration: '1 gün' });
    setShowNewTripModal(false);
  };

  const handleDeleteTrip = async (tripId) => {
    try {
      await tripsApi.delete(tripId);
    } catch (error) {
      console.log('Using local delete');
    }
    setTrips(trips.filter(t => t.id !== tripId));
    setSelectedTrip(null);
  };

  const handleAddPlace = async (place) => {
    if (!selectedTrip) return;
    
    try {
      await tripsApi.addPlace(selectedTrip, place.id);
    } catch (error) {
      console.log('Using local add');
    }
    
    const placeEntry = { id: place.id, name: place.name, completed: false };
    
    setTrips(trips.map(t => 
      t.id === selectedTrip 
        ? { ...t, places: [...t.places, placeEntry] }
        : t
    ));
    setShowAddPlaceModal(false);
    setPlaceSearch('');
  };

  const handleToggleComplete = async (tripId, placeId) => {
    try {
      await tripsApi.togglePlaceComplete(tripId, placeId);
    } catch (error) {
      console.log('Using local toggle');
    }
    
    setTrips(trips.map(t => 
      t.id === tripId 
        ? {
            ...t,
            places: t.places.map(p => 
              p.id === placeId ? { ...p, completed: !p.completed } : p
            )
          }
        : t
    ));
  };

  const handleRemovePlace = async (tripId, placeId) => {
    try {
      await tripsApi.removePlace(tripId, placeId);
    } catch (error) {
      console.log('Using local remove');
    }
    
    setTrips(trips.map(t => 
      t.id === tripId 
        ? { ...t, places: t.places.filter(p => p.id !== placeId) }
        : t
    ));
  };

  const getStopIcon = (type) => {
    switch (type) {
      case 'restaurant': return <Utensils className="w-4 h-4" />;
      case 'cafe': return <Coffee className="w-4 h-4" />;
      case 'hotel': return <Building className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const getStopColor = (type, hasCampaign) => {
    if (hasCampaign) return 'bg-yellow-500';
    switch (type) {
      case 'restaurant': return 'bg-orange-500';
      case 'cafe': return 'bg-amber-500';
      case 'hotel': return 'bg-purple-500';
      default: return 'bg-blue-500';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center p-4`}>
        <div className="text-center">
          <div className={`w-20 h-20 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Calendar className={`w-10 h-10 ${textSecondary}`} />
          </div>
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Gezilerinizi Planlayın</h2>
          <p className={`${textSecondary} mb-6`}>Gezi planları oluşturmak için giriş yapın</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#2a4a73] transition-colors"
          >
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  // Trip Detail View
  if (selectedTrip) {
    const trip = trips.find(t => t.id === selectedTrip);
    if (!trip) return null;

    const completedCount = trip.places.filter(p => p.completed).length;

    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        {/* Header */}
        <div className="relative h-48">
          <img src={trip.image} alt={trip.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <button
            onClick={() => setSelectedTrip(null)}
            className={`absolute top-4 left-4 w-10 h-10 ${darkMode ? 'bg-slate-800/90' : 'bg-white/90'} rounded-full flex items-center justify-center`}
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>
          {trip.ai_generated && (
            <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI Rota
            </div>
          )}
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h1 className="text-2xl font-bold mb-1">{trip.name}</h1>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" /> {trip.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> {trip.duration}
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4">
          {/* Progress */}
          {trip.places.length > 0 && (
            <div className={`${bgCard} rounded-2xl p-4 shadow-sm mb-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm ${textSecondary}`}>İlerleme</span>
                <span className="text-sm font-medium text-[#1e3a5f]">
                  {completedCount}/{trip.places.length} tamamlandı
                </span>
              </div>
              <div className={`h-2 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                <div 
                  className="h-full bg-[#1e3a5f] rounded-full transition-all duration-300"
                  style={{ width: `${(completedCount / trip.places.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Tips (if AI generated) */}
          {trip.tips && trip.tips.length > 0 && (
            <div className={`${bgCard} rounded-2xl p-4 shadow-sm mb-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-2`}>💡 İpuçları</h3>
              <ul className="space-y-1">
                {trip.tips.map((tip, i) => (
                  <li key={i} className={`text-sm ${textSecondary}`}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mb-6">
            <button className="flex-1 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium flex items-center justify-center gap-2">
              <Navigation className="w-5 h-5" />
              Rotayı Başlat
            </button>
            <button className={`p-3 ${bgCard} border ${borderColor} rounded-xl`}>
              <Share2 className={`w-5 h-5 ${textSecondary}`} />
            </button>
          </div>

          {/* Places */}
          <h2 className={`text-lg font-bold ${textPrimary} mb-4`}>Duraklar ({trip.places.length})</h2>
          <div className="space-y-3">
            {trip.places.map((place, index) => (
              <div key={place.id || index} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <button 
                    onClick={() => handleToggleComplete(trip.id, place.id)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      place.completed 
                        ? 'bg-green-500 text-white' 
                        : `${getStopColor(place.type, place.has_campaign)} text-white`
                    }`}
                  >
                    {place.completed ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      getStopIcon(place.type)
                    )}
                  </button>
                  {index < trip.places.length - 1 && (
                    <div className={`w-0.5 h-12 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} my-1`} />
                  )}
                </div>
                <div className={`flex-1 ${bgCard} rounded-xl p-4 shadow-sm`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${place.completed ? `${textSecondary} line-through` : textPrimary}`}>
                          {place.name}
                        </h3>
                        {place.has_campaign && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full flex items-center gap-1">
                            <Tag className="w-3 h-3" /> Kampanya
                          </span>
                        )}
                      </div>
                      {place.time && (
                        <p className={`text-sm ${textSecondary} mt-1`}>
                          🕐 {place.time} • {place.duration}
                        </p>
                      )}
                      {place.campaign_text && (
                        <p className="text-sm text-yellow-600 mt-1 font-medium">
                          🎁 {place.campaign_text}
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={() => handleRemovePlace(trip.id, place.id)}
                      className={`p-1 ${textSecondary} hover:text-red-500`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Place */}
          <button 
            onClick={() => setShowAddPlaceModal(true)}
            className={`w-full mt-4 py-3 border-2 border-dashed ${borderColor} rounded-xl ${textSecondary} font-medium flex items-center justify-center gap-2 hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-colors`}
          >
            <Plus className="w-5 h-5" />
            Durak Ekle
          </button>

          {/* Delete */}
          <button
            onClick={() => handleDeleteTrip(trip.id)}
            className="w-full mt-6 py-3 text-red-500 font-medium flex items-center justify-center gap-2"
          >
            <Trash2 className="w-5 h-5" />
            Geziyi Sil
          </button>
        </div>

        {/* Add Place Modal */}
        <Dialog open={showAddPlaceModal} onOpenChange={setShowAddPlaceModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Durak Ekle</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <div className="relative mb-4">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${textSecondary}`} />
                <Input
                  value={placeSearch}
                  onChange={(e) => setPlaceSearch(e.target.value)}
                  placeholder="Mekan ara..."
                  className="pl-10"
                />
              </div>
              <div className="space-y-2 max-h-60 overflow-auto">
                {filteredPlaces.map((place) => (
                  <button
                    key={place.id}
                    onClick={() => handleAddPlace(place)}
                    className={`w-full flex items-center gap-3 p-3 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} rounded-xl text-left transition-colors`}
                  >
                    <img src={place.image} alt={place.name} className="w-12 h-12 rounded-lg object-cover" />
                    <div>
                      <h4 className={`font-medium ${textPrimary}`}>{place.name}</h4>
                      <p className={`text-xs ${textSecondary}`}>{place.category_name}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      <div className="max-w-3xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>Gezilerim</h1>
            <p className={textSecondary}>Seyahat planlarınızı yönetin</p>
          </div>
          <button
            onClick={() => setShowNewTripModal(true)}
            className="w-12 h-12 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#2a4a73] transition-colors"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>

        {/* AI Trip Planner Card */}
        <div className="mb-6 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-2xl p-5 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-8 -mb-8" />
          
          <div className="relative z-10">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg">Yapay Zeka Destekli</h3>
                <p className="text-sm text-white/80">Kişisel seyahat planınızı saniyeler içinde oluşturun</p>
              </div>
            </div>
            
            {/* Features */}
            <div className="flex gap-4 mb-5 text-sm">
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-white/70" />
                <span className="text-white/90">Tarihinize göre plan</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Navigation className="w-4 h-4 text-white/70" />
                <span className="text-white/90">Optimize edilmiş rota</span>
              </div>
            </div>
            
            {/* Button */}
            <button
              onClick={() => setShowAiPlannerModal(true)}
              className="w-full py-3.5 bg-white text-purple-700 rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-purple-50 transition-colors shadow-lg"
            >
              <Sparkles className="w-5 h-5" />
              Plan Oluştur
            </button>
          </div>
        </div>

        {/* Ready Routes Card */}
        <div className={`mb-6 ${bgCard} rounded-2xl p-5 border ${borderColor} shadow-sm`}>
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Map className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className={`font-bold text-lg ${textPrimary}`}>Hazır Rotalar</h3>
              <p className={`text-sm ${textSecondary}`}>Uzmanlar tarafından hazırlanmış popüler rotalar</p>
            </div>
          </div>
          
          {/* Route previews */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {readyRoutes.slice(0, 3).map((route) => (
              <div 
                key={route.id}
                className="flex-shrink-0 w-20 text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-xl overflow-hidden mb-1">
                  <img src={route.image} alt={route.name} className="w-full h-full object-cover" />
                </div>
                <p className={`text-xs ${textSecondary} line-clamp-2`}>{route.name}</p>
              </div>
            ))}
            <div 
              className="flex-shrink-0 w-20 text-center cursor-pointer"
              onClick={() => setShowReadyRoutesModal(true)}
            >
              <div className={`w-16 h-16 mx-auto rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} flex items-center justify-center mb-1`}>
                <span className={`text-2xl ${textSecondary}`}>+{readyRoutes.length - 3}</span>
              </div>
              <p className={`text-xs ${textSecondary}`}>Daha fazla</p>
            </div>
          </div>
          
          {/* Button */}
          <button
            onClick={() => setShowReadyRoutesModal(true)}
            className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:from-emerald-600 hover:to-teal-700 transition-colors"
          >
            <Map className="w-5 h-5" />
            Hazır Rotaları Keşfet
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'upcoming'
                ? 'bg-[#1e3a5f] text-white'
                : `${bgCard} ${textSecondary} border ${borderColor}`
            }`}
          >
            Planlanan ({upcomingTrips.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'completed'
                ? 'bg-[#1e3a5f] text-white'
                : `${bgCard} ${textSecondary} border ${borderColor}`
            }`}
          >
            Tamamlanan ({completedTrips.length})
          </button>
        </div>

        {/* Trips List */}
        {displayTrips.length === 0 ? (
          <div className="text-center py-12">
            <div className={`w-20 h-20 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Map className={`w-10 h-10 ${textSecondary}`} />
            </div>
            <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>
              {activeTab === 'upcoming' ? 'Planlanmış gezi yok' : 'Tamamlanmış gezi yok'}
            </h3>
            <p className={`${textSecondary} mb-4`}>
              {activeTab === 'upcoming' ? 'Yeni bir gezi planlamaya başlayın!' : ''}
            </p>
            {activeTab === 'upcoming' && (
              <div className="flex gap-2 justify-center">
                <button
                  onClick={() => setShowNewTripModal(true)}
                  className="px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium"
                >
                  Manuel Oluştur
                </button>
                <button
                  onClick={() => setShowAiPlannerModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium flex items-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  AI ile Oluştur
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {displayTrips.map((trip) => (
              <div
                key={trip.id}
                onClick={() => setSelectedTrip(trip.id)}
                className={`${bgCard} rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
              >
                <div className="relative h-32">
                  <img src={trip.image} alt={trip.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <h3 className="font-bold text-lg">{trip.name}</h3>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> {trip.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {trip.places?.length || 0} durak
                      </span>
                    </div>
                  </div>
                  {trip.ai_generated && (
                    <div className="absolute top-3 left-3 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI
                    </div>
                  )}
                  {trip.status === 'completed' && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                      Tamamlandı
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Trip Modal */}
      <Dialog open={showNewTripModal} onOpenChange={setShowNewTripModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Gezi Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Gezi Adı</label>
              <Input
                value={newTrip.name}
                onChange={(e) => setNewTrip({ ...newTrip, name: e.target.value })}
                placeholder="Örn: Hafta sonu kaçamağı"
                className="h-12"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Tarih</label>
              <Input
                type="date"
                value={newTrip.date}
                onChange={(e) => setNewTrip({ ...newTrip, date: e.target.value })}
                className="h-12"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${textPrimary} mb-2`}>Süre</label>
              <select
                value={newTrip.duration}
                onChange={(e) => setNewTrip({ ...newTrip, duration: e.target.value })}
                className={`w-full h-12 px-3 border ${borderColor} rounded-lg ${darkMode ? 'bg-slate-800 text-white' : 'bg-white'}`}
              >
                <option value="1 gün">1 gün</option>
                <option value="2 gün">2 gün</option>
                <option value="3 gün">3 gün</option>
                <option value="1 hafta">1 hafta</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTripModal(false)}>
              İptal
            </Button>
            <Button onClick={handleCreateTrip} className="bg-[#1e3a5f] hover:bg-[#2a4a73]">
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Planner Modal */}
      <Dialog open={showAiPlannerModal} onOpenChange={(open) => {
        setShowAiPlannerModal(open);
        if (!open) {
          setAiPlan(null);
          setAiInterests([]);
          setAiPrompt('');
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Rota Planlayıcı
            </DialogTitle>
          </DialogHeader>
          
          {!aiPlan ? (
            <div className="space-y-6 py-4">
              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setAiMode('form')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    aiMode === 'form'
                      ? 'bg-purple-600 text-white'
                      : `${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${textSecondary}`
                  }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  Tercihler
                </button>
                <button
                  onClick={() => setAiMode('chat')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    aiMode === 'chat'
                      ? 'bg-purple-600 text-white'
                      : `${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${textSecondary}`
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  Sohbet
                </button>
              </div>

              {aiMode === 'form' ? (
                <>
                  {/* Duration */}
                  <div>
                    <label className={`block text-sm font-medium ${textPrimary} mb-2`}>
                      Kaç gün? 
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(d => (
                        <button
                          key={d}
                          onClick={() => setAiDuration(d)}
                          className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                            aiDuration === d
                              ? 'bg-purple-600 text-white'
                              : `${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${textSecondary}`
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Interests */}
                  <div>
                    <label className={`block text-sm font-medium ${textPrimary} mb-2`}>
                      İlgi alanlarınız
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {interestOptions.map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => toggleInterest(opt.id)}
                          className={`py-3 px-2 rounded-lg font-medium transition-colors text-sm ${
                            aiInterests.includes(opt.id)
                              ? 'bg-purple-600 text-white'
                              : `${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${textSecondary}`
                          }`}
                        >
                          {opt.icon} {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className={`block text-sm font-medium ${textPrimary} mb-2`}>
                    Ne tür bir gezi istiyorsunuz?
                  </label>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Örn: 2 günlük romantik bir gezi istiyorum, tarihi yerleri ve güzel restoranları görmek istiyorum..."
                    className={`w-full p-3 rounded-lg border ${borderColor} ${darkMode ? 'bg-slate-800 text-white' : ''} resize-none`}
                    rows={4}
                  />
                </div>
              )}

              <Button 
                onClick={handleGenerateAiTrip}
                disabled={aiLoading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-12"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Rota oluşturuluyor...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Rota Oluştur
                  </>
                )}
              </Button>
            </div>
          ) : (
            /* AI Plan Preview */
            <div className="py-4">
              <div className="mb-4">
                <h3 className={`text-xl font-bold ${textPrimary}`}>{aiPlan.name}</h3>
                <p className={`${textSecondary}`}>{aiPlan.duration}</p>
              </div>

              {/* Days */}
              <div className="space-y-4">
                {aiPlan.days?.map((day, dayIndex) => (
                  <div key={dayIndex} className={`${darkMode ? 'bg-slate-800' : 'bg-gray-50'} rounded-xl overflow-hidden`}>
                    <button
                      onClick={() => setExpandedDays(prev => ({ ...prev, [dayIndex]: !prev[dayIndex] }))}
                      className={`w-full p-4 flex items-center justify-between ${textPrimary}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {day.day}
                        </span>
                        <span className="font-semibold">{day.title}</span>
                      </div>
                      {expandedDays[dayIndex] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    
                    {expandedDays[dayIndex] && (
                      <div className="px-4 pb-4 space-y-3">
                        {day.stops?.map((stop, stopIndex) => (
                          <div key={stopIndex} className={`${bgCard} rounded-lg p-3`}>
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 ${getStopColor(stop.type, stop.has_campaign)} text-white rounded-full flex items-center justify-center flex-shrink-0`}>
                                {getStopIcon(stop.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className={`font-semibold ${textPrimary}`}>{stop.name}</h4>
                                  {stop.has_campaign && (
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                                      🎁 Kampanya
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm ${textSecondary}`}>
                                  🕐 {stop.time} • {stop.duration}
                                </p>
                                {stop.description && (
                                  <p className={`text-sm ${textSecondary} mt-1`}>{stop.description}</p>
                                )}
                                {stop.campaign_text && (
                                  <p className="text-sm text-yellow-600 font-medium mt-1">
                                    {stop.campaign_text}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Tips */}
              {aiPlan.tips && aiPlan.tips.length > 0 && (
                <div className={`mt-4 ${darkMode ? 'bg-slate-800' : 'bg-blue-50'} rounded-xl p-4`}>
                  <h4 className={`font-semibold ${textPrimary} mb-2`}>💡 İpuçları</h4>
                  <ul className="space-y-1">
                    {aiPlan.tips.map((tip, i) => (
                      <li key={i} className={`text-sm ${textSecondary}`}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setAiPlan(null)}
                  className="flex-1"
                >
                  Yeniden Oluştur
                </Button>
                <Button
                  onClick={handleSaveAiTrip}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ready Routes Modal */}
      <Dialog open={showReadyRoutesModal} onOpenChange={(open) => {
        setShowReadyRoutesModal(open);
        if (!open) {
          setSelectedReadyRoute(null);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Map className="w-5 h-5 text-emerald-500" />
              Hazır Rotalar
            </DialogTitle>
          </DialogHeader>
          
          {!selectedReadyRoute ? (
            <div className="py-4">
              <p className={`${textSecondary} mb-4`}>Uzmanlar tarafından hazırlanmış rotalardan birini seçin</p>
              
              <div className="grid gap-4">
                {readyRoutes.map((route) => (
                  <div
                    key={route.id}
                    onClick={() => handleSelectReadyRoute(route)}
                    className={`${bgCard} rounded-xl overflow-hidden border ${borderColor} hover:border-emerald-500 cursor-pointer transition-colors`}
                  >
                    <div className="flex">
                      <img src={route.image} alt={route.name} className="w-32 h-32 object-cover" />
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className={`font-semibold ${textPrimary}`}>{route.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              route.category === 'Tarihi' ? 'bg-amber-100 text-amber-700' :
                              route.category === 'Doğa' ? 'bg-green-100 text-green-700' :
                              route.category === 'Yeme-İçme' ? 'bg-orange-100 text-orange-700' :
                              route.category === 'Fotoğraf' ? 'bg-blue-100 text-blue-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {route.category}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className={`text-sm font-medium ${textPrimary}`}>{route.rating}</span>
                          </div>
                        </div>
                        <p className={`text-sm ${textSecondary} line-clamp-2 mb-2`}>{route.description}</p>
                        <div className={`flex items-center gap-4 text-xs ${textSecondary}`}>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {route.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {route.places.length} durak
                          </span>
                          <span>{route.difficulty}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Route Detail View */
            <div className="py-4">
              {/* Header */}
              <div className="relative h-40 rounded-xl overflow-hidden mb-4">
                <img src={selectedReadyRoute.image} alt={selectedReadyRoute.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h2 className="text-xl font-bold">{selectedReadyRoute.name}</h2>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {selectedReadyRoute.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> {selectedReadyRoute.rating}
                    </span>
                    <span>{selectedReadyRoute.reviewCount} yorum</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className={`${textSecondary} mb-4`}>{selectedReadyRoute.description}</p>

              {/* Places */}
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Rota Durakları</h3>
              <div className="space-y-3 mb-4">
                {selectedReadyRoute.places.map((place, index) => (
                  <div key={place.id} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        place.type === 'restaurant' ? 'bg-orange-500' :
                        place.type === 'cafe' ? 'bg-amber-500' :
                        place.has_campaign ? 'bg-yellow-500' :
                        'bg-emerald-500'
                      }`}>
                        {place.day ? `${place.day}.${index + 1}` : index + 1}
                      </div>
                      {index < selectedReadyRoute.places.length - 1 && (
                        <div className={`w-0.5 h-8 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} my-1`} />
                      )}
                    </div>
                    <div className={`flex-1 ${bgCard} rounded-lg p-3 border ${borderColor}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${textPrimary}`}>{place.name}</span>
                        {place.has_campaign && (
                          <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                            🎁 {place.campaign_text}
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${textSecondary}`}>
                        🕐 {place.time} • {place.duration}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tips */}
              {selectedReadyRoute.tips && (
                <div className={`${darkMode ? 'bg-slate-800' : 'bg-emerald-50'} rounded-xl p-4 mb-4`}>
                  <h4 className={`font-semibold ${textPrimary} mb-2`}>💡 İpuçları</h4>
                  <ul className="space-y-1">
                    {selectedReadyRoute.tips.map((tip, i) => (
                      <li key={i} className={`text-sm ${textSecondary}`}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedReadyRoute(null)}
                  className="flex-1"
                >
                  Geri
                </Button>
                <Button
                  onClick={handleAddReadyRouteToTrips}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Gezilerime Ekle
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTripsPage;
